import { ChevronDownIcon, ChevronUpIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { MouseEventHandler, PropsWithChildren, useState } from "react";
import { FormattedMessage } from "react-intl";

export interface ExpandableListProps extends PropsWithChildren {
  total: number;
  maxVisible?: number;
  isExpanding?: boolean;
  noExpand?: boolean;
  onExpand?: () => void;
}

export default function ExpandableList({
  total,
  maxVisible = 5,
  onExpand,
  isExpanding,
  noExpand,
  children,
}: ExpandableListProps) {
  const [expanded, setExpanded] = useState(false);
  const items = Array.isArray(children) ? children : [children];
  const visibleItems = expanded ? items : items.slice(0, maxVisible);

  const toggle: MouseEventHandler = (event) => {
    event.preventDefault();
    if (!expanded && onExpand) {
      onExpand();
    }
    setExpanded(!expanded);
  };

  return (
    <div className="flex gap-2">
      <ul className="flex flex-wrap items-center gap-2">
        {visibleItems}
        {total > maxVisible && (
          <li className="flex flex-row items-center gap-2">
            {!expanded && (
              <p className="flex justify-start text-grey-400 text-sm font-semibold leading-tight">
                ...
              </p>
            )}
            {!noExpand && (
              <Button
                variant="link"
                hue="grey"
                size="sm"
                onClick={toggle}
                disabled={isExpanding}
              >
                {expanded ? (
                  <>
                    <FormattedMessage defaultMessage="See less" />
                    <ChevronUpIcon />
                  </>
                ) : (
                  <>
                    <FormattedMessage
                      defaultMessage="See all ({count})"
                      values={{ count: total }}
                    />
                    <ChevronDownIcon />
                  </>
                )}
              </Button>
            )}
          </li>
        )}
      </ul>
    </div>
  );
}
